import { useEffect, useState } from 'react';

import { Alert, Container } from '@mui/material';
import { saveAs } from 'file-saver';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { monitoringApis } from '../../../apis/monitoringApis.js';
import { serverApis } from '../../../apis/serverApis.js';
import Loading from '../../../components/elements/loading/Loading';
import MonitoringData from '../../../components/page-sections/monitoring/MonitoringData';
import MonitoringSettings from '../../../components/page-sections/monitoring/MonitoringSettings';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { DEFAULT_SUBSCRIPT_NUMBER } from '../../../constants';
import { useProject } from '../../../contexts/ProjectContext.jsx';
import { useUser } from '../../../contexts/UserContext';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { localDateTimeToUTC } from '../../../utils/timeUtils';

const ServerMonitoringPage = () => {
  // Initial data
  const { projectId, gpsId } = useParams();
  const { user } = useUser();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const { project } = useProject();
  const [gps, setGps] = useState(null);
  const [error, setError] = useState(false);
  const showToast = useToast();

  // Monitoring settings
  const [settings, setSettings] = useState({
    node: DEFAULT_SUBSCRIPT_NUMBER,
    term: 900,
    endDateTime: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
    refresh: 0,
  });

  // Monitoring data
  const [monitorError, setMonitorError] = useState(false);
  const [monitoringData, setMonitoringData] = useState(null);

  /**
   * Load initial data for the page
   * @returns {Promise<void>}
   */
  const loadInitialData = async () => {
    setLoading(true);
    try {
      const { data: gps } = await serverApis.getServer(projectId, gpsId);
      setGps(gps);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // At initial render, load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  /**
   * Handle the time period input changes. This input sets the time period for the monitoring data. 15 min, 30 min, etc.
   * @param term - The time period 15 min, 30 min, etc.
   */
  const handleTermChange = (term) => {
    setSettings({ ...settings, term: term });
  };
  /**
   * Handle the date time input changes. This input sets the END date time for the monitoring data.
   * @param dateTime - The date time input value.
   */
  const handleDateTimeChange = (dateTime) => {
    setSettings({ ...settings, endDateTime: dateTime });
  };

  /**
   * Handle the refresh rate input changes. This input sets the refresh rate for the monitoring data.
   * @param refresh - The refresh rate input value. 15 sec, 30 sec, etc.
   */
  const handleRefreshChange = (refresh) => {
    setSettings({ ...settings, refresh: refresh });
  };

  const handleFetchMonitoringData = async () => {
    const setStep = (term) => (term / 100 < 15 ? 15 : term / 100);
    const params = {
      ...settings,
      step: setStep(settings.term),
      endDateTime: localDateTimeToUTC(
        settings.endDateTime,
        user?.timeZone,
        'YYYY-MM-DDTHH:mm:ss'
      ),
    };

    try {
      const { data: response } = await monitoringApis.getMetrics(
        projectId,
        gps.id,
        'application/json',
        params
      );
      setMonitoringData(response);
      setMonitorError(false);
    } catch (error) {
      setMonitorError(true);
    }
  };

  useEffect(() => {
    if (!gps) return;
    handleFetchMonitoringData();
  }, [gps, settings]);

  useEffect(() => {
    if (settings.refresh === 0) return;
    // Refresh monitoring data every refresh rate
    const interval = setInterval(() => {
      handleDateTimeChange(
        moment().tz(user?.timeZone).format('YYYY-MM-DDTHH:mm:ss')
      );
    }, settings.refresh);
    return () => clearInterval(interval);
  }, [settings.refresh, settings.term]);

  /**
   * Handle the download button click. This function will download the monitoring data
   * @returns {Promise<void>}
   */
  const handleDownload = async () => {
    try {
      const params = {
        ...settings,
        step: settings.term / 100,
        endDateTime: localDateTimeToUTC(
          settings.endDateTime,
          user?.timeZone,
          'YYYY-MM-DDTHH:mm:ss'
        ),
      };

      const { data: response } = await monitoringApis.getMetrics(
        projectId,
        gps.id,
        'text/csv',
        params
      );

      const blob = new Blob([response], {
        type: 'text/csv',
      });
      saveAs(blob, moment(settings.endDateTime).format('YYYY-MM-DD HH_mm_ss'));
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        t('fetchMetricsError', { ns: 'notifications' })
      );
    }
  };

  if (loading) return <Loading />;

  if (error)
    return (
      <Alert style={{ marginTop: '1em' }} severity="error">
        {t('fetchServerError', { ns: 'notifications' })}
      </Alert>
    );

  return (
    project &&
    gps && (
      <Container>
        <Breadcrumbs projectName={project.name} serverName={gps.name} />
        <TitlePanel title={t('monitor', { ns: 'titles' })} />
        <MonitoringSettings
          instance={gps}
          settings={settings}
          setSettings={setSettings}
          onDateTimeChange={handleDateTimeChange}
          onTermChange={handleTermChange}
          onRefreshChange={handleRefreshChange}
        />
        {(monitoringData || monitorError) && (
          <MonitoringData
            monitoringData={monitoringData}
            onDownload={handleDownload}
            error={monitorError}
          />
        )}
      </Container>
    )
  );
};

export default ServerMonitoringPage;
